import * as React from 'react'
import {ReactNode} from 'react'
import {Nullable} from '../../../../../app/TypeHelpers'
import {BlockSpacer} from '../../../../../styledComponents/ResponsiveConvenience'
import {APPLY_CATEGORY, DESTINATION_TYPE} from '../../../../../CodeMaster'
import {convertToRoomNumberForResident} from '../../../../common/util/Natutil'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey
} from '../../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {
    getFormattedAddress,
    getFormattedDate
} from '../../contactAddressOnEmergencies/helper/NatContactAddressOnEmergencyHelper'
import NatResidentApplicationDetailConfirmItem from '../../common/resident/NatResidentApplicationDetailConfirmItem'

interface Props {
    applicantFamilyName: string
    applicantFirstName: string
    absenceStartDate: string
    absenceEndDate: string
    applicantContactPhoneNumber: string
    destinationType: string
    destinationZipCode: Nullable<string>
    destinationPrefecture: Nullable<string>
    destinationAddress2: Nullable<string>
    destinationAddress3: Nullable<string>
    destinationBuildingRoomNumber: Nullable<string>
    destinationAddress: Nullable<string>
    contactFamilyName: Nullable<string>
    contactFirstName: Nullable<string>
    contactRelationship: Nullable<string>
    contactZipCode: Nullable<string>
    contactPrefecture: Nullable<string>
    contactAddress2: Nullable<string>
    contactAddress3: Nullable<string>
    contactBuildingRoomNumber: Nullable<string>
    contactPhoneNumber: Nullable<string>
    applyFlag?: boolean
}

const NatResidentLongAbsenceConfirm = ({applyFlag = true, ...otherProps}: Props) => {
    const {
        applicantFamilyName, applicantFirstName, absenceStartDate, absenceEndDate,
        applicantContactPhoneNumber, destinationType, destinationZipCode, destinationPrefecture,
        destinationAddress2, destinationAddress3, destinationBuildingRoomNumber, destinationAddress,
        contactFamilyName, contactFirstName, contactRelationship, contactZipCode,
        contactPrefecture, contactAddress2, contactAddress3, contactBuildingRoomNumber,
        contactPhoneNumber
    } = otherProps

    const applyCategoryLabel = APPLY_CATEGORY.LONG_ABSENCE.label
    const applicantRoom = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME) + '　' +
        NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_RIDGENAME) + ' ' +
        convertToRoomNumberForResident(
            NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER)
        )
    const absenceStartDateLabel = (absenceStartDate) ? getFormattedDate(absenceStartDate) : ''
    const absenceEndDateLabel = (absenceEndDate) ? getFormattedDate(absenceEndDate) : ''
    const applicantContactPhoneNumberLabel = (applicantContactPhoneNumber) ? applicantContactPhoneNumber : ''
    const destinationTypeLabel = (destinationType) ? DESTINATION_TYPE.codeToEnumConstants(destinationType).label : ''
    const destinationZipCodeLabel = (destinationZipCode) ? destinationZipCode : ''
    const destinationPrefectureLabel = (destinationPrefecture) ? destinationPrefecture : ''
    const destinationAddress2Label = (destinationAddress2) ? destinationAddress2 : ''
    const destinationAddress3Label = (destinationAddress3) ? destinationAddress3 : ''
    const destinationBuildingRoomNumberLabel = (destinationBuildingRoomNumber) ? destinationBuildingRoomNumber : ''
    const destinationAddressLabel = (destinationAddress) ? destinationAddress : ''
    const contactFamilyNameLabel = (contactFamilyName) ? contactFamilyName : ''
    const contactFirstNameLabel = (contactFirstName) ? contactFirstName : ''
    const contactRelationshipLabel = (contactRelationship) ? contactRelationship : ''
    const contactZipCodeLabel = (contactZipCode) ? contactZipCode : ''
    const contactPrefectureLabel = (contactPrefecture) ? contactPrefecture : ''
    const contactAddress2Label = (contactAddress2) ? contactAddress2 : ''
    const contactAddress3Label = (contactAddress3) ? contactAddress3 : ''
    const contactBuildingRoomNumberLabel = (contactBuildingRoomNumber) ? contactBuildingRoomNumber : ''
    const contactPhoneNumberLabel = (contactPhoneNumber) ? contactPhoneNumber : ''

    const absenceDateInfo = (): ReactNode => {
        const items = []
        items.push(`開始日：${absenceStartDateLabel}`)
        items.push(`終了日：${absenceEndDateLabel}`)

        return <>
            <NatResidentApplicationDetailConfirmItem
                title={'不在期間'}
                items={items}
            />
        </>
    }
    const destinationInfo = (): ReactNode => {
        const items = []
        items.push(`${getFormattedAddress(
            destinationZipCodeLabel,
            destinationPrefectureLabel,
            destinationAddress2Label,
            destinationAddress3Label,
            destinationBuildingRoomNumberLabel)}`
        )

        return <>
            <NatResidentApplicationDetailConfirmItem
                title={'滞在先の住所'}
                items={items}
            />
        </>
    }
    const contactInfo = (): ReactNode => {
        const items = []
        items.push(`氏名：${contactFamilyNameLabel} ${contactFirstNameLabel}`)
        items.push(`続柄：${contactRelationshipLabel}`)
        items.push(`住所：${getFormattedAddress(
            contactZipCodeLabel,
            contactPrefectureLabel,
            contactAddress2Label,
            contactAddress3Label,
            contactBuildingRoomNumberLabel)}`
        )
        items.push(`電話番号：${contactPhoneNumberLabel}`)

        return <>
            <NatResidentApplicationDetailConfirmItem
                title={'滞在先の住所'}
                items={[destinationAddressLabel]}
            />
            <BlockSpacer height={'5px'}/>
            <NatResidentApplicationDetailConfirmItem
                title={'日本国内の連絡先'}
                items={items}
            />
        </>
    }

    return (<>
        {applyFlag === true &&
            <>
                <NatResidentApplicationDetailConfirmItem
                    title={'申請区分'}
                    items={[applyCategoryLabel]}
                />
                <BlockSpacer height={'5px'}/>
                <NatResidentApplicationDetailConfirmItem
                    title={'申請者'}
                    items={[applicantRoom, `${applicantFamilyName} ${applicantFirstName}`]}
                />
            </>
        }
        <BlockSpacer height={'5px'}/>
        {absenceDateInfo()}
        <BlockSpacer height={'5px'}/>
        <NatResidentApplicationDetailConfirmItem
            title={'不在中の連絡先（本人）'}
            items={[applicantContactPhoneNumberLabel]}
        />
        <BlockSpacer height={'5px'}/>
        <NatResidentApplicationDetailConfirmItem
            title={'滞在先'}
            items={[destinationTypeLabel]}
        />
        <BlockSpacer height={'5px'}/>
        {(destinationType === DESTINATION_TYPE.DOMESTIC.value) &&
            <>{destinationInfo()}</>
        }
        {(destinationType === DESTINATION_TYPE.OVERSEA.value) &&
            <>{contactInfo()}</>
        }
    </>)
}

NatResidentLongAbsenceConfirm.displayName = 'NatResidentLongAbsenceConfirm'
export default NatResidentLongAbsenceConfirm